import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useI18n } from '../context/I18nContext';

// Tela Insights - doc 13 secao 3.
//
// As metricas vem em dois pedidos separados (conta e por post) porque a Meta
// derruba a chamada inteira quando UMA metrica nao esta disponivel. O backend
// ja faz o fallback para o conjunto reduzido; aqui a tela so nao pode sumir
// por causa de um post sem numero, entao cada bloco falha sozinho.

const MetaInsights = () => {
  const { t } = useI18n();
  const [conexoes, setConexoes] = useState([]);
  const [conexaoId, setConexaoId] = useState('');
  const [conta, setConta] = useState(null);
  const [posts, setPosts] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/meta-connections')
      .then((r) => {
        const igs = (r.data || []).filter(
          (c) => c.status === 'connected' && c.platform === 'instagram'
        );
        setConexoes(igs);
        if (igs.length) setConexaoId(String(igs[0].id));
      })
      .catch(() => setErro(t('metainbox.a2')));
  }, [t]);

  async function carregar() {
    setCarregando(true); setErro(''); setConta(null); setPosts([]);
    try {
      const c = await api.get(`/meta-connections/${conexaoId}/insights`);
      setConta(c.data);
    } catch (err) {
      const e = err.response && err.response.data && err.response.data.error;
      setErro((e && e.message) || err.message);
    }
    try {
      const p = await api.get(`/meta-connections/${conexaoId}/insights/posts`);
      setPosts(p.data.posts || []);
    } catch (err) {
      // Lista de posts falhar nao pode apagar os numeros da conta que ja vieram.
      console.warn('[Insights] posts indisponiveis', err.message);
    }
    setCarregando(false);
  }

  const num = (v) => (typeof v === 'number' ? v.toLocaleString() : '—');

  const Card = ({ rotulo, valor }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{rotulo}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{num(valor)}</p>
    </div>
  );

  return (
    <div className="p-8 max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('ins.title')}</h1>

      {conexoes.length === 0 ? (
        <p className="text-gray-500">{t('ins.onlyIg')}</p>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6 flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-900 mb-2">{t('ins.account')}</label>
              <select value={conexaoId} onChange={(e) => setConexaoId(e.target.value)}
                className="w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm">
                {conexoes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={carregar} disabled={carregando}
              className="px-5 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50">
              {carregando ? t('common.loading') : t('ins.load')}
            </button>
          </div>

          {erro && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{erro}</div>
          )}

          {!conta && !carregando && !erro && (
            <p className="text-sm text-gray-500">{t('ins.empty')}</p>
          )}

          {conta && (
            <>
              <p className="text-sm text-gray-500 mb-3">{t('ins.last28', { v1: conta.periodoDias })}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card rotulo={t('ins.followers')} valor={conta.perfil.followers_count} />
                <Card rotulo={t('ins.follows')} valor={conta.perfil.follows_count} />
                <Card rotulo={t('ins.mediaCount')} valor={conta.perfil.media_count} />
                <Card rotulo={t('ins.reach')} valor={conta.metricas.reach} />
                <Card rotulo={t('ins.views')} valor={conta.metricas.views} />
                <Card rotulo={t('ins.engaged')} valor={conta.metricas.accounts_engaged} />
                <Card rotulo={t('ins.interactions')} valor={conta.metricas.total_interactions} />
                <Card rotulo={t('ins.profileViews')} valor={conta.metricas.profile_views} />
              </div>
            </>
          )}

          {posts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <h2 className="text-lg font-semibold text-gray-900 p-6 pb-3">{t('ins.byPost')}</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-6 py-3">{t('ins.post')}</th>
                      <th className="text-right px-4 py-3">{t('ins.reach')}</th>
                      <th className="text-right px-4 py-3">{t('ins.likes')}</th>
                      <th className="text-right px-4 py-3">{t('meta.comments')}</th>
                      <th className="text-right px-4 py-3">{t('ins.saved')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.id} className="border-t border-gray-100">
                        <td className="px-6 py-3">
                          <a href={p.link} target="_blank" rel="noreferrer"
                            className="text-gray-800 hover:underline line-clamp-1 block max-w-md">
                            {p.legenda || p.tipo}
                          </a>
                        </td>
                        <td className="text-right px-4 py-3">{num(p.metricas.reach)}</td>
                        <td className="text-right px-4 py-3">{num(p.metricas.likes)}</td>
                        <td className="text-right px-4 py-3">{num(p.metricas.comments)}</td>
                        <td className="text-right px-4 py-3">{num(p.metricas.saved)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MetaInsights;
